/**
 * Persisted SMS browser device identity.
 *
 * Mechanics live in `@aegix/web-push`; this module owns SMS's
 * single-owner storage key: this module is the only minter. Consumers are
 * the FCM register/deregister calls (`x-device-id` header + `device_id`
 * payload field) and the SMS transport
 * (`lib/deviceIdHeader.ts`), which stamps the READ-ONLY view on outbound
 * requests. The backend uses the id to sweep a device's stale
 * registrations on register/deregister and to bind sessions to a device.
 *
 * Deliberately NOT AIM's `device_id` key: the two apps can share a browser
 * profile (same origin in some envs), and a shared key would let one
 * product's logout/deregister sweep the other's registration.
 * Storage-unavailable fallback is a per-call mint
 * (`'per-call'`) — an unstable identity only weakens the backend's device
 * sweep, which is a state it handles.
 */
import { createDeviceIdStore } from '@aegix/web-push';

export const SMS_DEVICE_ID_STORAGE_KEY = 'aegix_sms_device_id';

const store = createDeviceIdStore({
  storageKey: SMS_DEVICE_ID_STORAGE_KEY,
  ephemeralFallback: 'per-call',
});

/** The persisted SMS device id, minting one on first use. */
export const getSmsDeviceId = store.getOrCreate;

/**
 * Read-only view for callers that must NOT invent an identity — the
 * transport stamping. A browser with nothing persisted sends no
 * device header at all rather than minting one on a plain API call
 * (mirrors AIM's `getPersistedDeviceId`): the backend reads a
 * missing device id as "legacy session, no per-device scoping", a state
 * it handles.
 */
export const getPersistedSmsDeviceId = store.getPersisted;
